import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { faCheck, faLayerGroup, faSliders, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { forkJoin, of } from 'rxjs';
import * as _ from 'underscore';
import { AuthService } from '../../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { BlockDTO } from '../../../../../core/models/block';
import { CropVariety } from '../../../../../core/models/crop-variety';
import { CropYear } from '../../../../../core/models/cropYear';
import { BlockService } from '../../../../../core/services/block.service';
import { CropYearService } from '../../../../../core/services/crop-year.service';
import { VarietyService } from '../../../../../core/services/variety.service';
import { PlatformConfig } from '../../../../../core/models/platform-config';
import { PlatformConfigService } from '../../../../../core/services/platform-config.service';
import { LoadViewModel } from '../../../../pick-plan/models/load-view-model';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { LoadService } from '../../../../pick-plan/services/load.service';
import { KwFilterDropdownService } from '../../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { HandlingUnitDetailDTO } from '../../../models/handling-unit-detail';
import { HandlingUnitMasterDTO } from '../../../models/handling-unit-master';
import { InventoryLotAttributeValue, InventoryLotDTO } from '../../../models/inventory-lot';
import { InventoryItemDTO } from '../../../models/item';
import { InventoryLotAttributeDTO } from '../../../models/lot-attribute';
 
export interface InventoryLotPopupData {
  lot: InventoryLotDTO;
  isPopup: boolean;
  isReturnBottomsheet?: boolean;
  isHumEdit: boolean;
  isBulkCreate: boolean;
  hum?: HandlingUnitMasterDTO;
  hud?: HandlingUnitDetailDTO;
}

@Component({
  selector: 'inventory-lot-edit',
  templateUrl: './inventory-lot-edit.component.html',
  styleUrls: ['./inventory-lot-edit.component.scss']
})
export class InventoryLotEditComponent implements OnInit {
  @ViewChild('lotInputForm', { static: false }) lotInputForm: NgForm;

  private destroyRef = inject(DestroyRef);
  constructor(@Inject(MAT_DIALOG_DATA) public data: InventoryLotPopupData,
    private activatedRoute: ActivatedRoute,
    private inventoryService: InventoryService,
    private router: Router,
    private blocksService: BlockService,
    private loadService: LoadService,
    private dialogRef: MatDialogRef<InventoryLotEditComponent>,
    private cropYearService: CropYearService,
    private changeDetRef: ChangeDetectorRef,
    private location: Location,
    public authService: AuthService,
    private varietyService: VarietyService,
    private errorService: ErrorService,
    private platformConfigService: PlatformConfigService,
    private filterService: KwFilterDropdownService,) {
    if (data.lot)
      this.lot = data.lot;
    else
      this.lot = {};
  }

  // Icons  //
  public faXmark = faXmark;
  public faLayerGroup = faLayerGroup;
  public faSliders = faSliders;
  public faTrash = faTrash;
  public faCheck = faCheck;

  public lot: InventoryLotDTO = {};
  public years: CropYear[];
  public blocks: BlockDTO[];
  public lotAttrabutes: InventoryLotAttributeDTO[];
  public loads: LoadViewModel[];
  public varieties: CropVariety[];
  public isLoading: boolean = true;
  public lots: InventoryLotDTO[] = [];
  public accessionItems: InventoryItemDTO[] = [];

  /** Lower-cased field names to omit from this form, shared with the Lots List grid's "Inventory Lots List Hidden Columns" PlatformConfig (comma-separated). Matched against hardcoded field labels (e.g. "Load", "Block", "Generation") and custom attribute names. */
  private hiddenFieldNames: string[] = [];

  ngOnInit() {

    this.inventoryService.getInventoryItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(items => {
      this.accessionItems = items.filter(x => x.parentItemName == 'Accession');
    })

    

    forkJoin([
      this.blocksService.apiBlocksGet(null, false),
      this.cropYearService.getCropYears(false),
      this.authService.hasRole("harvest:read") ? this.loadService.getLoads(false, false, null, '') : of([]),
      this.inventoryService.getInventoryLotAttributes(),
      this.inventoryService.getLots(),
      this.varietyService.getVarieties(),
      this.platformConfigService.getAllConfigValues(),

      //this.roomService.getRooms()
    ])
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:(x:any) => {
        this.blocks = x[0];
        this.years = x[1];
        this.loads = x[2];
        this.lotAttrabutes = x[3];
        this.lots = x[4];


        this.varieties = _.sortBy(x[5], y => y.name);
        this.hiddenFieldNames = this.parseHiddenFieldNames(x[6]);
        //this.isLoading = false;
        if (!this.lot.id)
          this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            if (params["lotId"]) {
              this.inventoryService.getInventoryLot(params["lotId"]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
                this.lot = x;
                this.isLoading = false;
                this.changeDetRef.detectChanges();
              }, error:err => {
                this.errorService.handleError(err);
                this.isLoading = false;
                this.changeDetRef.detectChanges();
              }});
            }
            this.customFields();
            this.isLoading = false;
            this.changeDetRef.detectChanges();
          })
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }, error:err => {
        this.errorService.handleError(err);
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }});
  }

  submit() {
    this.isLoading = true;
    if (this.lot.id) {
      this.inventoryService.putInventoryLot(this.lot).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        // Keep the filter dropdown cache in sync so other pages show the change immediately
        this.filterService.upsertCached('lot', this.lot);
        this.cancel();
      }, error:err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
    }
    else {
      this.inventoryService.postInventoryLot(this.lot).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        this.lot.id = x.id;
        this.filterService.upsertCached('lot', this.lot);
        this.cancel();
      }, error:err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
    }
  }

  cancel() {
    if (this.data.isPopup) {
      if (this.lot.id) {
        // Add Product (or Create Inventory's Add Item section) is still open under this dialog;
        // it reloads lots on close and reselects data.hud.lotId.
        if (this.data.isReturnBottomsheet)
          this.data.hud.lotId = this.lot.id;
        this.dialogRef.close(this.lot);
      }
      else {
        this.dialogRef.close();
      }
    }
    else
      this.location.back();
  }

  deleteLot() {
    this.isLoading = true;
    this.inventoryService.deleteInventoryLot(this.lot.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.filterService.removeCached('lot', this.lot.id);
      this.cancel();
    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
    }});
  }

  customFields() {
    if (!this.lot.attributes)
      this.lot.attributes = [];

    this.lotAttrabutes.forEach(f => {

      var tempField = this.lot.attributes.find(x => x.id == f.id);
      if (tempField) {
        tempField.customAttributeDescription = f.description;
        tempField.customAttributeName = f.name;
        tempField.id = this.lot.id;
      }
      else {
        let fieldValue: InventoryLotAttributeValue = {
          customAttributeDatatype: f.datatype,
          customAttributeDescription: f.description,
          customAttributeName: f.name,
          id: 0,
          customAttributeId: f.id,
          isActive: true
        };
        this.lot.attributes.push(fieldValue);
      }
    });
  }

  private parseHiddenFieldNames(configs: PlatformConfig[]): string[] {
    let config = configs?.find(c => c.name === 'Inventory Lots List Hidden Columns' && c.isActive !== false);

    if (!config || !config.value)
      return [];

    return config.value.split(',').map(v => v.trim().toLowerCase()).filter(v => v.length > 0);
  }

  public isFieldHidden(name: string): boolean {
    return this.hiddenFieldNames.includes((name || '').toLowerCase());
  }

  getOptionsLength(attrabute) {
    if (this.lotAttrabutes) {
      var t = this.lotAttrabutes.find(x => x.id == attrabute.customAttributeId);
      if (t && t.options)
        return t.options.length;
      else
        return 0;
    }
    else return 0;
  }

  ClearValue(field) {
    var currentField = this.lot.attributes.find(x => x.id == field.id);
    currentField.value = null;
  }

  getFieldName(field) {
    var currentField = this.lotAttrabutes.find(x => x.id == field.customAttributeId);
    return currentField.name
  }

  getFieldOptions(field) {
    var currentField = this.lotAttrabutes.find(x => x.id == field.customAttributeId);
    currentField.options = currentField.options.sort((a, b) => a.display.localeCompare(b.display));
    return currentField.options
  }
}
